import { at } from '../core/clock';
import type { Rng } from '../core/rng';
import { HURRICANE_NAMES, type Difficulty } from './config';

/*
 * The night's script. Every shift has the same beats (the club lets out, it
 * becomes 3 AM, somebody throws a chair) but the seed decides the details —
 * so the daily shift is identical for everyone who plays it.
 */

export type ScriptedEventId =
  | 'openingLine'
  | 'regular'
  | 'proposal'
  | 'eggSurcharge'
  | 'sirThisIsAWaffleHouse'
  | 'streamer'
  | 'clubLetOut'
  | 'fight'
  | 'witchingHour'
  | 'host'
  | 'weatherman'
  | 'hurricane'
  | 'raccoonHour'
  | 'newHostSpam'
  | 'jukebox'
  | 'churchLadies'
  | 'lastCall'
  | 'returning';

export interface ScheduledEvent {
  id: ScriptedEventId;
  /** Shift minutes at which it fires. */
  at: number;
  fired: boolean;
  /** Event-specific payload (e.g. which returning customer). */
  data?: number;
}

export interface NightPlan {
  events: ScheduledEvent[];
  hurricaneName: string;
  fightCount: number;
}

const between = (rng: Rng, from: number, to: number): number => Math.round(rng.range(from, to));

export function planNight(rng: Rng, difficulty: Difficulty): NightPlan {
  const events: ScheduledEvent[] = [];
  const add = (id: ScriptedEventId, minute: number): void => {
    events.push({ id, at: minute, fired: false });
  };

  add('openingLine', 1);
  add('regular', between(rng, at(22, 35), at(23, 20)));
  if (rng.chance(0.6)) add('proposal', between(rng, at(23, 10), at(0, 50)));
  if (rng.chance(0.55)) add('eggSurcharge', between(rng, at(23, 40), at(1, 20)));
  add('sirThisIsAWaffleHouse', between(rng, at(0, 30), at(1, 40)));
  if (rng.chance(0.75)) add('streamer', between(rng, at(1, 35), at(1, 55)));
  add('clubLetOut', at(2, 0));
  add('fight', between(rng, at(2, 8), at(2, 40)));
  add('witchingHour', at(3, 0));
  add('host', at(3, 33));

  // The storm is the night's centrepiece — FEMA's Waffle House Index is the
  // whole premise — so it always makes landfall; the seed picks when.
  const landfall = between(rng, at(3, 48), at(4, 12));
  add('weatherman', landfall - between(rng, 9, 15));
  add('hurricane', landfall);
  let fightCount = 1;
  if (rng.chance(difficulty.secondFightChance)) {
    add('fight', between(rng, at(5, 5), at(5, 28)));
    fightCount = 2;
  }
  if (rng.chance(0.2)) add('raccoonHour', between(rng, at(3, 10), at(5, 20)));
  add('newHostSpam', between(rng, at(23, 0), at(5, 30)));
  add('jukebox', between(rng, at(22, 50), at(1, 30)));
  add('jukebox', between(rng, at(2, 45), at(5, 10)));
  add('churchLadies', at(5, 0));
  add('lastCall', at(5, 45));

  events.sort((a, b) => a.at - b.at);
  return { events, hurricaneName: rng.pick(HURRICANE_NAMES), fightCount };
}

export const TICKER_FILLER = [
  'LOCAL COOK "DID NOT LOOK UP" DURING BRAWL, WITNESSES CONFIRM',
  'STUDY: 100% OF 3AM DECISIONS MADE AT WAFFLE HOUSE',
  'WAFFLE HOUSE DOORS "STILL DO NOT LOCK," OFFICIALS SAY',
  'SCIENTISTS CONFIRM HASH BROWNS CAN BE SCATTERED, SMOTHERED AND COVERED AT THE SAME TIME',
  'AREA JUKEBOX HAS PLAYED "RAISINS IN MY TOAST" 400 TIMES TONIGHT',
  'MAN WHO ORDERED A SALAD "STILL RECOVERING"',
  'FEMA: "IF THE WAFFLE HOUSE IS OPEN, YOU\'RE GONNA BE FINE"',
  'EXPERTS WARN: THE CLUB LETS OUT AT 2',
  'GRITS: SUGAR OR SALT? DEBATE ENTERS 200TH YEAR',
  'WAFFLE HOUSE STAFF RANKED "TOUGHER THAN THE AVENGERS" IN POLL NOBODY TOOK',
  'LOCAL MAN SAYS HE "DIDN\'T START IT"',
  'CHAIR-CATCHING ADDED TO LIST OF PROPOSED OLYMPIC SPORTS',
  'ALL THE WAY: IS IT TOO MUCH? (NO.)',
  'BREAKING: IT IS, IN FACT, 3AM SOMEWHERE',
  'PECAN PRONUNCIATION DISPUTE CONTINUES; NO INJURIES REPORTED YET',
  'REPORT: CUSTOMER WALKED BEHIND COUNTER AND COOKED OWN MEAL; STAFF "RESTING EYES"',
  'EGG PRICES "DOING WHATEVER THEY WANT," ECONOMISTS ADMIT',
  'THE WAFFLE HOUSE HAS FOUND ITS NEW HOST',
] as const;
